import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Loader2, RotateCcw, X } from "lucide-react";
import {
  restoreBulk,
  type BulkBody,
  type BulkResponse,
  type DeletedObject,
} from "@/lib/recycleApi";

const MIN_REASON = 16;

/**
 * Bulk-restore modal. Three phases:
 *
 * <ol>
 *   <li><b>Compose</b> — per-item rename input + shared reason field
 *       + Submit. The compose phase shows blockers per item that the
 *       backend's per-item validator will reject.</li>
 *   <li><b>Running</b> — single backend call (the controller runs
 *       items sequentially; the UI shows a spinner with the batch
 *       count). When a future server-side streaming variant ships,
 *       this view will switch to per-item progress.</li>
 *   <li><b>Receipt</b> — per-item outcomes table with green-check
 *       for OK rows and red badge for failed rows. The operator can
 *       screenshot it; closing returns to the list view.</li>
 * </ol>
 */
export function BulkRestoreModal({
  open,
  selected,
  onClose,
  onComplete,
}: {
  open: boolean;
  selected: DeletedObject[];
  onClose: () => void;
  onComplete: (summary: BulkResponse) => void;
}) {
  const qc = useQueryClient();

  // Per-store-id rename input (operator can override per item).
  const [renames, setRenames] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<BulkResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    setRenames({});
    setReason("");
    setError(null);
    setReceipt(null);
  }, [open]);

  const submit = useMutation({
    mutationFn: () => {
      const body: BulkBody = {
        items: selected.map((s) => ({
          storeId: s.storeId,
          alternateName: renames[s.storeId]?.trim() || null,
        })),
        reason: reason.trim(),
      };
      return restoreBulk(body);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["recycle-list"] });
      setReceipt(data);
      onComplete(data);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg.includes("403") ? "Admin role required for bulk restore." : msg);
    },
  });

  // Esc closes (only when not running).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submit.isPending) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, submit.isPending]);

  if (!open) return null;

  const reasonValid = reason.trim().length >= MIN_REASON;
  const submitDisabled = !reasonValid || selected.length === 0 || submit.isPending;

  // Receipt-mode summary
  const succeeded = receipt?.succeeded ?? 0;
  const failed = receipt?.failed ?? 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-label="Bulk restore"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submit.isPending) onClose();
      }}
    >
      <div className="flex h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5">
        <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">
              {receipt
                ? `Bulk restore — ${succeeded} succeeded · ${failed} failed`
                : `Bulk restore — ${selected.length} item${selected.length === 1 ? "" : "s"}`}
            </div>
            <div className="text-xs text-slate-500">
              {receipt
                ? `Completed in ${receipt.elapsedMs} ms. Sequential per-item; not transactional.`
                : "Sequential per-item, not transactional. Each item runs independently and the result reports per-item outcomes."}
            </div>
          </div>
          <button
            type="button"
            disabled={submit.isPending}
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </header>

        {/* Compose / running / receipt body */}
        {!receipt ? (
          <div className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
            <div className="mb-3">
              <label className="text-2xs font-medium uppercase tracking-wider text-slate-500">
                Reason ({MIN_REASON}+ chars · applies to every item, audit-logged)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Bulk restore per ticket OPS-3421"
                disabled={submit.isPending}
                maxLength={1000}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50"
              />
              {!reasonValid && reason.length > 0 && (
                <p className="mt-0.5 text-[10px] text-rose-600">
                  {MIN_REASON - reason.trim().length} more characters needed.
                </p>
              )}
            </div>

            <table className="w-full text-xs">
              <thead className="border-b border-slate-200 text-2xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-1.5 text-left font-medium">Object</th>
                  <th className="py-1.5 text-left font-medium">Original parent</th>
                  <th className="py-1.5 text-left font-medium">Alternate name</th>
                  <th className="py-1.5 text-left font-medium">Pre-flight</th>
                </tr>
              </thead>
              <tbody>
                {selected.map((s) => {
                  const blockerHint = pickBlockerHint(s);
                  return (
                    <tr key={s.storeId} className="border-b border-slate-100">
                      <td className="py-1.5 align-top">
                        <div className="font-medium text-slate-900">{s.name ?? "—"}</div>
                        <div className="font-mono text-[10px] text-slate-500">{s.storeId}</div>
                      </td>
                      <td className="py-1.5 align-top text-slate-700">
                        <span className="font-mono text-[10px]">
                          {s.originalParentDisplayPath ?? "—"}
                        </span>
                      </td>
                      <td className="py-1.5 align-top">
                        <input
                          type="text"
                          value={renames[s.storeId] ?? ""}
                          onChange={(e) =>
                            setRenames((r) => ({ ...r, [s.storeId]: e.target.value }))
                          }
                          placeholder={s.name ?? "(keep original)"}
                          disabled={submit.isPending}
                          maxLength={256}
                          className="w-44 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                        />
                      </td>
                      <td className="py-1.5 align-top">
                        {blockerHint ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                            title={blockerHint}
                          >
                            <AlertTriangle size={9} />
                            {blockerHint}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
                            <Check size={9} /> Ready
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {error && (
              <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
                {error}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
            <table className="w-full text-xs">
              <thead className="border-b border-slate-200 text-2xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-1.5 text-left font-medium">Status</th>
                  <th className="py-1.5 text-left font-medium">Store id</th>
                  <th className="py-1.5 text-left font-medium">Restored to</th>
                  <th className="py-1.5 text-left font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {receipt.outcomes.map((o, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5 align-top">
                      {o.status === "OK" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
                          <Check size={9} /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-800">
                          <AlertTriangle size={9} /> {o.status}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 align-top">
                      <span className="font-mono text-[10px] text-slate-600">{o.storeId ?? "—"}</span>
                    </td>
                    <td className="py-1.5 align-top">
                      <span className="font-mono text-[10px] text-slate-700">
                        {o.restoredToPath ?? "—"}
                      </span>
                    </td>
                    <td className="py-1.5 align-top text-slate-700">
                      {o.errorMessage ?? (o.status === "OK" ? `Renamed to ${o.name}` : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          {!receipt && (
            <button
              type="button"
              onClick={onClose}
              disabled={submit.isPending}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
            >
              Cancel
            </button>
          )}
          {!receipt && (
            <button
              type="button"
              disabled={submitDisabled}
              onClick={() => {
                setError(null);
                submit.mutate();
              }}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submit.isPending && <Loader2 size={11} className="animate-spin" />}
              <RotateCcw size={11} />
              Restore {selected.length} item{selected.length === 1 ? "" : "s"}
            </button>
          )}
          {receipt && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              Done
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/** Best-effort pre-flight blocker hint shown in the compose table. */
function pickBlockerHint(s: DeletedObject): string | null {
  if (s.recoverability === "past_retention") return "Past retention";
  if (!s.parentStillExists) return "Parent gone";
  return null;
}
